<template>
  <div class="offer-highlights">
    <ProductContentDetails
      v-if="displayOfferUsps || displayProductUsps"
      :offer="offer"
      :render-item-if="(product) => updateUsps(product)?.length > 0"
    >
      <template #default="slotProps">
        <div
          class="offer-highlights__row"
          :class="{
            'offer-highlights__row--half': hasSingleVideo(
              slotProps.product?.media || slotProps.offer?.media
            ),
          }"
        >
          <div class="offer-highlights__list-column">
            <BulletPointList
              :list="updateUsps(slotProps.product)"
              itemprop="description"
            />
            <div class="offer-highlights__links">
              <KameraLink
                :offer="slotProps.offer"
                class="offer-highlights__kamera-link"
              />
            </div>
          </div>
          <OfferHighlightVideos
            v-if="slotProps.product?.media || props.offer.media"
            :media="slotProps.product.media || props.offer.media"
            class="offer-highlights__list-column"
          />
        </div>

        <!-- If the product itself is a bundle, the description is shown on "product-details" -->
        <ContentToggle
          v-if="slotProps.product.description && !slotProps.product.bundle"
          no-border
          class="offer-highlights__content-toggle"
        >
          <article
            class="offer-highlights__description unstyled-html"
            :data-testid="`bundle-description-${slotProps.product.family.slug}`"
            v-html="slotProps.product.description"
          />
        </ContentToggle>
      </template>
    </ProductContentDetails>
  </div>
</template>

<script setup lang="ts">
import { offerHasOwnUsps, offerHasProductUsps } from '~/lib/goliath/offer-usps';
import BulletPointList from '~/components/bullet-point-list.vue';
import ProductContentDetails from '~/components/product-detail/tabs/product-content-details.vue';
import ContentToggle from '~/components/content-toggle.vue';
import OfferHighlightVideos from '~/components/product-detail/tabs/offer-highlight-videos.vue';
import type { Attachment } from '#shared/types/goliath/goliath.types';
import KameraLink from '~/components/kamera-link.vue';

type OfferHighlightsProps = {
  offer: Offer;
};

const props = defineProps<OfferHighlightsProps>();

const displayOfferUsps = computed(() => props.offer && offerHasOwnUsps(props.offer));

const displayProductUsps = computed(
  () => !displayOfferUsps.value && props.offer && offerHasProductUsps(props.offer)
);

const getVideos = (media: Attachment[]) => {
  if (!media) return [];
  return media.filter((mediaObject) => mediaObject.remote && mediaObject.type === 'VIDEO');
};
const hasSingleVideo = (media: Attachment[]) => getVideos(media).length === 1;

const updateUsps = (product: OfferProduct | undefined) => {
  if (!product) return [];
  const infoMessages = {
    wifiCompatible:
      '**Wichtiger Hinweis:** Das Gerät wird über WLAN eingebunden, es wird keine weitere Hardware benötigt',
    compatibilityRequirements:
      '**Wichtiger Hinweis:** Für die Einbindung des Gerätes ist weitere Hardware z. B. eine Bridge/Gateway oder eine Zentrale nötig.',
  };
  const isWifiCompatible = product.tags?.includes('i22-wifi-compatible');
  const isSmartHomeProduct = product.family?.rootCategories?.some(
    (category) => category?.slug === 'smart-home'
  );
  if (!isSmartHomeProduct) return product.usps;
  return isWifiCompatible
    ? [...product.usps, infoMessages.wifiCompatible]
    : [...product.usps, infoMessages.compatibilityRequirements];
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
@use '@i22team/td-longtail-vue-components/dist/styles/pattern/text';

.offer-highlights {
  &__row {
    display: grid;
    grid-gap: $grid-base;

    &--half {
      @include mq($mq-md) {
        grid-template-columns: 1fr 1fr;
      }
    }
  }

  &__list-column {
    :deep(.list__element) a {
      @extend .link;

      &[target='_blank'] {
        @extend .link--external;
      }
    }
  }

  &__content-toggle {
    margin: 4 * $grid-base 0;
    padding-left: 0;

    @include mq($mq-md) {
      margin: 6 * $grid-base 0;
    }
  }

  &__description {
    columns: 456px 3;
    column-gap: 48px;

    @include mq($mq-sm) {
      margin-bottom: 4 * $grid-base;
    }
    :deep(p) {
      line-height: 1.6;
    }
    :deep(p + h2, p + h3) {
      margin-top: 3rem;
    }
  }
  &__links {
    @include mq($mq-sm) {
      display: flex;
      justify-content: stretch;
    }
  }

  &__kamera-link {
    margin-left: 1.5 * $grid-base;
  }
}
</style>
